/**
 * UX Analytics Service — Phase 1
 *
 * Records journey telemetry to Firestore `ux_events` collection.
 * All calls are fire-and-forget (non-blocking, errors are silently swallowed
 * so telemetry never disrupts the user flow).
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

// ── Event Type Registry ───────────────────────────────────────────────────
export type UxEventType =
  | 'JOURNEY_START'
  | 'JOURNEY_COMPLETE'
  | 'JOURNEY_ABANDON'
  | 'DEAD_END_HIT'
  | 'PERMISSION_DENIED'
  | 'TASK_SUCCESS'
  | 'TASK_FAILURE'
  | 'FIRST_VALUE'
  | 'FORM_ERROR'
  | 'FEATURE_USED'
  | 'ONBOARDING_STEP'
  | 'ROUTE_VIEW'
  | 'RUNTIME_ERROR';

export interface UxEventPayload {
  eventType: UxEventType;
  journeyId?: string;
  route?: string;
  reason?: string;
  durationMs?: number;
  metadata?: Record<string, unknown>;
}

// ── Internal state (populated via init()) ────────────────────────────────
let _tenantId = '';
let _userId = '';
let _role = '';
let _deviceType = '';
let _sessionId = '';
let _routeEntryId = '';

function createCorrelationId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function ensureSessionId() {
  if (_sessionId) {
    return _sessionId;
  }

  _sessionId = createCorrelationId('session');
  return _sessionId;
}

/**
 * Call once after auth resolves to bind user context to all future events.
 */
export function initAnalytics(ctx: {
  tenantId: string;
  userId: string;
  role: string;
  deviceType: string;
  sessionId?: string;
}) {
  _tenantId = ctx.tenantId;
  _userId = ctx.userId;
  _role = ctx.role;
  _deviceType = ctx.deviceType;
  _sessionId = ctx.sessionId?.trim() || ensureSessionId();
}

export function getAnalyticsSessionId(): string {
  return ensureSessionId();
}

export function setRouteTrace(route: string): string {
  _routeEntryId = createCorrelationId('route');
  trackEvent({
    eventType: 'ROUTE_VIEW',
    route,
    metadata: {
      routeEntryId: _routeEntryId,
    },
  });
  return _routeEntryId;
}

export function trackRuntimeError(route: string, reason: string, metadata?: Record<string, unknown>): void {
  trackEvent({
    eventType: 'RUNTIME_ERROR',
    route,
    reason,
    metadata,
  });
}

/**
 * Track a generic UX event.
 */
export async function trackEvent(payload: UxEventPayload): Promise<void> {
  if (!_userId) return; // Not yet authenticated — skip silently
  try {
    await addDoc(collection(db, 'ux_events'), {
      tenantId: _tenantId,
      userId: _userId,
      role: _role,
      deviceType: _deviceType,
      sessionId: ensureSessionId(),
      routeEntryId: _routeEntryId || null,
      timestamp: serverTimestamp(),
      ...payload,
    });
  } catch {
    // Telemetry must never throw — silently discard errors
  }
}

// ── Convenience helpers ───────────────────────────────────────────────────

export function trackDeadEnd(route: string, reason: string): void {
  trackEvent({ eventType: 'DEAD_END_HIT', route, reason });
}

export function trackPermissionDenied(route: string, reason: string): void {
  trackEvent({ eventType: 'PERMISSION_DENIED', route, reason });
}

export function trackTaskSuccess(journeyId: string, durationMs?: number): void {
  trackEvent({ eventType: 'TASK_SUCCESS', journeyId, durationMs });
}

export function trackTaskFailure(journeyId: string, reason: string): void {
  trackEvent({ eventType: 'TASK_FAILURE', journeyId, reason });
}

export function trackFormError(journeyId: string, fieldErrors: string[]): void {
  trackEvent({ eventType: 'FORM_ERROR', journeyId, metadata: { fieldErrors } });
}

export function trackFirstValue(feature: string): void {
  trackEvent({ eventType: 'FIRST_VALUE', metadata: { feature } });
}

export function trackOnboardingStep(step: string): void {
  trackEvent({ eventType: 'ONBOARDING_STEP', metadata: { step } });
}

export function trackFeatureUsed(feature: string, metadata?: Record<string, unknown>): void {
  trackEvent({ eventType: 'FEATURE_USED', metadata: { feature, ...metadata } });
}
